// Use environment variables for Supabase configuration
const API_BASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export class EventbriteApiService {
  constructor(userToken) {
    this.userToken = userToken;
  }

  // Sync attendees from Eventbrite using the actual edge function
  async syncEventAttendees(eventbriteId) {
    try {
      console.log('Starting Eventbrite sync for event:', eventbriteId);
      
      const response = await fetch(`${API_BASE_URL}/functions/v1/eventbrite_attendee_sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.userToken}`
        },
        body: JSON.stringify({ 
          event_id: eventbriteId.toString() 
        })
      });

      console.log('Sync response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Sync failed:', errorData);
        throw new Error(errorData.error || 'Failed to sync attendees');
      }

      const result = await response.json();
      console.log('Sync successful:', result);
      return result;
    } catch (error) {
      console.error('Eventbrite sync error:', error);
      throw error;
    }
  }

  // Check if attendees data exists for an event
  async checkAttendeesExist(eventbriteId) {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/event_attendees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          eventbrite_id: parseInt(eventbriteId),
          limit_val: 1,
          offset_val: 0,
          search_query: ''
        })
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data && data.attendees && data.attendees.length > 0;
    } catch (error) {
      console.error('Error checking attendees existence:', error);
      return false;
    }
  }

  // Get attendees using the actual PostgreSQL function
  async getEventAttendees(eventbriteId, options = {}) {
    const {
      searchQuery = '',
      limitVal = 50,
      offsetVal = 0
    } = options;

    try {
      console.log('Calling event_attendees RPC with:', {
        eventbrite_id: parseInt(eventbriteId),
        limit_val: limitVal,
        offset_val: offsetVal,
        search_query: searchQuery
      });
      
      // Check if eventbriteId is valid
      if (!eventbriteId || isNaN(parseInt(eventbriteId))) {
        console.error('Invalid eventbriteId:', eventbriteId);
        throw new Error('Invalid eventbrite ID');
      }

      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/event_attendees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          eventbrite_id: parseInt(eventbriteId),
          limit_val: limitVal,
          offset_val: offsetVal,
          search_query: searchQuery
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('RPC call failed:', errorText);
        throw new Error(`Failed to fetch attendees: ${errorText}`);
      }

      const data = await response.json();
      console.log('RPC response data:', data);
      
      // Check if we have attendees data
      if (data && data.attendees) {
        console.log(`Found ${data.attendees.length} attendees`);
        console.log('Sample attendee:', data.attendees[0]);
      } else {
        console.log('No attendees data in response:', data);
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching attendees:', error);
      throw error;
    }
  }

  // Check-in/check-out attendee using the actual edge function
  async updateAttendeeCheckin(eventbriteId, ticketId, checkedIn) {
    try {
      const requestBody = {
        event_id: eventbriteId.toString(),
        ticket_id: ticketId.toString(),
        checked_in: checkedIn
      };
      
      console.log('Sending check-in request:', requestBody);
      console.log('Calling URL:', `${API_BASE_URL}/functions/v1/eventbrite_attendee_checkin`);
      
      const response = await fetch(`${API_BASE_URL}/functions/v1/eventbrite_attendee_checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.userToken}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Check-in response error:', errorData);
        throw new Error(errorData.error || 'Failed to update check-in status');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Check-in update error:', error);
      throw error;
    }
  }

  // Sync all events from Eventbrite organization using the actual edge function
  async syncAllEvents() {
    try {
      const response = await fetch(`${API_BASE_URL}/functions/v1/eventbrite_events_sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.userToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to sync events');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Events sync error:', error);
      throw error;
    }
  }

  // Get event details using the actual function
  async getEventDetails(eventId) {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_event_details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.userToken}`,
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          event_id_param: eventId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch event details');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching event details:', error);
      throw error;
    }
  }

  // Check if Eventbrite event exists and has attendees (mock for now)
  async checkEventbriteEvent(eventId) {
    try {
      console.log('Checking Eventbrite event:', eventId);
      
      // For now, let's just log the event ID to verify it's being passed correctly
      // In a real implementation, you'd call Eventbrite's API directly
      console.log('Eventbrite event ID to check:', eventId);
      
      return {
        exists: true, // Assume it exists for now
        attendee_count: 0, // We'll need to check this
        event_name: 'Unknown Event'
      };
    } catch (error) {
      console.error('Error checking Eventbrite event:', error);
      return {
        exists: false,
        attendee_count: 0,
        event_name: 'Unknown Event'
      };
    }
  }
}

// Utility functions for attendee management
export const AttendeeUtils = {
  // Export attendees to CSV
  exportAttendeesToCSV(attendees, eventName) {
    const csvData = attendees.map(attendee => {
      const hasCheckedInTickets = (attendee.tickets || []).some(t => t.checked_in);
      const allCheckedIn = (attendee.tickets || []).every(t => t.checked_in);
      
      return {
        'First Name': attendee.attendee_first_name,
        'Last Name': attendee.attendee_last_name,
        'Email': attendee.attendee_email,
        'Handle': attendee.handle || '',
        'Total Tickets': attendee.num_tickets,
        'Ticket Type': attendee.ticket_tier,
        'Check-in Status': allCheckedIn ? 'All Checked In' : 
                          hasCheckedInTickets ? 'Partially Checked In' : 'Not Checked In',
        'VibrantCast User': attendee.is_in_system ? 'Yes' : 'No',
        'Order Date': attendee.order_date ? new Date(attendee.order_date).toLocaleDateString() : '',
        'Individual Tickets': (attendee.tickets || []).map(t => 
          `${t.ticket_id}: ${t.checked_in ? 'Checked In' : 'Not Checked In'}`
        ).join('; ')
      };
    });
    
    this.downloadCSV(csvData, `${eventName.replace(/[^a-z0-9]/gi, '_')}_attendees.csv`);
  },

  // Download CSV helper
  downloadCSV(data, filename) {
    // Convert data to CSV string
    const headers = Object.keys(data[0] || {});
    const csvRows = [headers.join(',')];
    
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header] || '';
        // Escape commas and quotes in CSV
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    });
    
    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  // Format attendee data for display
  formatAttendeeForDisplay(attendee) {
    return {
      ...attendee,
      fullName: `${attendee.attendee_first_name} ${attendee.attendee_last_name}`,
      checkInStatus: this.getCheckInStatus(attendee.tickets),
      orderDateFormatted: attendee.order_date ? 
        new Date(attendee.order_date).toLocaleDateString() : 'Unknown',
    };
  },

  // Get overall check-in status for multiple tickets
  getCheckInStatus(tickets) {
    if (!tickets || tickets.length === 0) return 'no_tickets';
    
    const checkedInCount = tickets.filter(t => t.checked_in).length;
    
    if (checkedInCount === 0) return 'none_checked_in';
    if (checkedInCount === tickets.length) return 'all_checked_in';
    return 'partially_checked_in';
  },

  // Calculate check-in statistics
  calculateStats(attendees) {
    const stats = {
      totalAttendees: attendees.length,
      totalTickets: attendees.reduce((sum, a) => sum + (a.num_tickets || 0), 0),
      checkedInAttendees: 0,
      checkedInTickets: 0,
      vibrantCastUsers: attendees.filter(a => a.is_in_system).length,
      externalUsers: attendees.filter(a => !a.is_in_system).length
    };

    attendees.forEach(attendee => {
      const checkedInTicketCount = (attendee.tickets || []).filter(t => t.checked_in).length;
      
      if (checkedInTicketCount > 0) {
        stats.checkedInAttendees++;
      }
      
      stats.checkedInTickets += checkedInTicketCount;
    });

    stats.checkInPercentage = stats.totalTickets > 0 
      ? Math.round((stats.checkedInTickets / stats.totalTickets) * 100)
      : 0;

    return stats;
  },

  // Filter attendees based on criteria
  filterAttendees(attendees, filters) {
    return attendees.filter(attendee => {
      // Status filter
      if (filters.status) {
        const status = this.getCheckInStatus(attendee.tickets);
        if (filters.status === 'checked_in' && status === 'none_checked_in') return false;
        if (filters.status === 'not_checked_in' && status !== 'none_checked_in') return false;
      }

      // User type filter
      if (filters.userType) {
        if (filters.userType === 'system_user' && !attendee.is_in_system) return false;
        if (filters.userType === 'external_user' && attendee.is_in_system) return false;
      }

      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const searchableText = [
          attendee.attendee_first_name,
          attendee.attendee_last_name,
          attendee.attendee_email,
          attendee.handle || ''
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(searchTerm)) return false;
      }

      return true;
    });
  }
};

// React Hook for Event Attendee Management
export const useEventAttendees = (eventDetails, userToken) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiService = new EventbriteApiService(userToken);

  const fetchAttendees = async (options = {}) => {
    if (!eventDetails?.eventbrite_id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiService.getEventAttendees(eventDetails.eventbrite_id, options);
      setAttendees(data.attendees || []);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching attendees:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncAttendees = async () => {
    if (!eventDetails?.eventbrite_id) return;

    setLoading(true);
    setError(null);

    try {
      await apiService.syncEventAttendees(eventDetails.eventbrite_id);
      await fetchAttendees(); // Refresh after sync
      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Error syncing attendees:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateCheckinStatus = async (orderId, checkedIn) => {
    if (!eventDetails?.eventbrite_id) return;

    try {
      await apiService.updateAttendeeCheckin(eventDetails.eventbrite_id, orderId, checkedIn);
      await fetchAttendees(); // Refresh after update
      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Error updating check-in:', err);
      return { success: false, error: err.message };
    }
  };

  // Load attendees on mount
  useEffect(() => {
    if (eventDetails?.eventbrite_id) {
      fetchAttendees();
    }
  }, [eventDetails?.eventbrite_id]);

  return {
    attendees,
    loading,
    error,
    fetchAttendees,
    syncAttendees,
    updateCheckinStatus
  };
};

// Notification helpers (integrate with your existing notification system)
export const NotificationService = {
  showSuccess(message) {
    // Replace with your existing success notification system
    // For example, if you use react-toastify:
    // toast.success(message);
    console.log('✅ Success:', message);
  },

  showError(message) {
    // Replace with your existing error notification system
    // For example, if you use react-toastify:
    // toast.error(message);
    console.error('❌ Error:', message);
  },

  showInfo(message) {
    // Replace with your existing info notification system
    console.log('ℹ️ Info:', message);
  }
};

// Event management constants
export const EVENT_CONSTANTS = {
  CHECKIN_STATUSES: {
    ALL_CHECKED_IN: 'all_checked_in',
    PARTIALLY_CHECKED_IN: 'partially_checked_in',
    NONE_CHECKED_IN: 'none_checked_in',
    NO_TICKETS: 'no_tickets'
  },
  
  FILTER_OPTIONS: {
    STATUS: {
      ALL: '',
      CHECKED_IN: 'checked_in',
      NOT_CHECKED_IN: 'not_checked_in'
    },
    USER_TYPE: {
      ALL: '',
      SYSTEM_USER: 'system_user',
      EXTERNAL_USER: 'external_user'
    }
  },

  PAGINATION: {
    DEFAULT_LIMIT: 50,
    DEFAULT_OFFSET: 0
  }
};